import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcwIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { cn } from '@/lib/utils.js';
import {
  ArtifactRevisionConflictError,
  useRestoreArtifactRevision,
  workspaceArtifactRevisionsQueryOptions,
  type ArtifactRevision,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import { useArtifactAuthorName } from './artifact-author.js';
import { formatBytes } from './artifact-display.js';

/** What the panel is currently telling the user about the last restore, if anything. */
type RestoreStatus = { tone: 'ok' | 'error'; text: string } | null;

/**
 * Said when a restore loses the optimistic lock. The history the user was looking at is out of date, so
 * the fix is to look again — not to retry the same call, which would race the same way.
 */
const CONFLICT_TEXT =
  'Someone saved a newer version while this was open. The history has been refreshed — pick a version and restore it again.';

/**
 * One artifact's version history: every stored version with its author and date, any of them viewable in
 * the preview, and — for a caller who may write — restorable.
 *
 * **A restore appends; it never rolls back.** Bringing version 1 of a three-version file back leaves the
 * file with four versions, and the wording here says so: nothing in the history is ever discarded, which
 * is what makes restore a safe click with no confirmation step.
 *
 * Reading the history is reading, so it is open to anyone who can see the file. `canRestore` is the
 * server's write matrix mirrored in `canEditArtifact` — when it is false no restore control is rendered at
 * all, rather than one that 403s.
 */
export function ArtifactVersionHistory({
  workspaceId,
  artifact,
  canRestore,
  selectedRevision,
  onSelectRevision,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  canRestore: boolean;
  /** Which version the preview is showing — null means the head, which is the default. */
  selectedRevision: number | null;
  onSelectRevision: (revision: number | null) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery(
    workspaceArtifactRevisionsQueryOptions(workspaceId, artifact.id),
  );
  const authorName = useArtifactAuthorName(workspaceId);
  const restore = useRestoreArtifactRevision(workspaceId);
  const [status, setStatus] = useState<RestoreStatus>(null);
  const revisions = query.data ?? [];

  /**
   * Restore one version. `base_revision` is the head we believe we are appending to, so a save that
   * landed while this panel was open comes back as a conflict instead of overwriting that save. On a
   * conflict the history is re-read: the whole point is that the user decides again against fresh facts.
   */
  function restoreRevision(revision: number): void {
    setStatus(null);
    restore.mutate(
      { id: artifact.id, revision, baseRevision: artifact.headRevision },
      {
        onSuccess: (updated) => {
          onSelectRevision(null);
          setStatus({
            tone: 'ok',
            text: `Version ${revision} is back as version ${updated.headRevision}. Nothing was deleted.`,
          });
        },
        onError: (error) => {
          if (error instanceof ArtifactRevisionConflictError) {
            void queryClient.invalidateQueries({
              queryKey: ['workspace-artifacts', workspaceId],
            });
            setStatus({ tone: 'error', text: CONFLICT_TEXT });
            return;
          }
          setStatus({ tone: 'error', text: error.message });
        },
      },
    );
  }

  return (
    <section aria-label="Version history" className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        Every save adds a version. Restoring one brings its contents back as a
        new version — the versions after it stay in this list.
      </p>
      {status && (
        <p
          className={cn(
            'text-xs',
            status.tone === 'error' ? 'text-destructive' : 'text-primary',
          )}
        >
          {status.text}
        </p>
      )}
      {query.isLoading ? (
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-muted-foreground">
          Couldn't load the version history. Try again shortly.
        </p>
      ) : (
        <ul className="max-h-56 divide-y divide-border/60 overflow-y-auto rounded-lg border border-border">
          {revisions.map((revision) => (
            <RevisionRow
              key={revision.revision}
              revision={revision}
              author={authorName(revision.createdBy)}
              isHead={revision.revision === artifact.headRevision}
              isViewing={
                (selectedRevision ?? artifact.headRevision) ===
                revision.revision
              }
              canRestore={canRestore}
              busy={restore.isPending}
              onView={() =>
                onSelectRevision(
                  revision.revision === artifact.headRevision
                    ? null
                    : revision.revision,
                )
              }
              onRestore={() => restoreRevision(revision.revision)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One version in the list: which version it is, who saved it and when, how big it was, and what can be
 * done with it. The head carries a "Latest" badge and no restore control — restoring the version that is
 * already current would only duplicate it.
 *
 * **The row itself is the control that switches the shown version**, so the whole line is the click target
 * rather than a small icon at its end. Restore is its SIBLING inside the list item, never a child of it: a
 * `<button>` inside a `<button>` is invalid HTML and breaks assistive tech, and it would also make Restore
 * select the row on the way through. Being a native button is what gives the row Tab, Enter and Space for
 * free; `aria-pressed` says which version is on screen.
 *
 * The hover tint sits on the LIST ITEM, not on that button: the button is only the left part of the line,
 * so tinting it left the strip under Restore untouched and the highlight stopped mid-row. The version on
 * screen carries its own tint instead of a hover one — it is a state, not a pointer cue.
 */
function RevisionRow({
  revision,
  author,
  isHead,
  isViewing,
  canRestore,
  busy,
  onView,
  onRestore,
}: {
  revision: ArtifactRevision;
  /** Who saved it, already resolved to a display name — never the raw identity id. */
  author: string;
  isHead: boolean;
  isViewing: boolean;
  canRestore: boolean;
  busy: boolean;
  onView: () => void;
  onRestore: () => void;
}): JSX.Element {
  return (
    <li
      className={cn(
        'flex items-center gap-1 pr-2 transition-colors',
        isViewing ? 'bg-primary/10' : 'hover:bg-muted',
      )}
    >
      <button
        type="button"
        onClick={onView}
        aria-label={`View version ${revision.revision}`}
        aria-pressed={isViewing}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-3 py-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-foreground">
              Version {revision.revision}
            </span>
            {isHead && (
              <Badge variant="secondary" className="text-[10px]">
                Latest
              </Badge>
            )}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {`${author} · ${new Date(revision.createdAt).toLocaleString()} · ${formatBytes(revision.size)}`}
          </span>
        </span>
      </button>
      {canRestore && !isHead ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={onRestore}
          aria-label={`Restore version ${revision.revision} as a new version`}
        >
          <RotateCcwIcon className="size-3.5" /> Restore
        </Button>
      ) : null}
    </li>
  );
}
